import React, { useState, useEffect, useMemo } from 'react';
import { useProperty } from '../context/PropertyContext.js';
import { parse, format } from 'date-fns';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Collapse,
  Divider,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  ArrowBack,
  DateRange,
  ExpandMore,
  ExpandLess,
  Warning,
  Analytics,
  Adjust,
  Edit
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { IP } from '../configuracion.js';
import AjusteChequeForm from './AjusteChequeForm.js';
import '../styles/CorteDeCaja.css';

const BASE_API_URL = `${IP}/tavpos/backend`;

const CorteAdmin = ({ show, onClose }) => {
  const { property } = useProperty();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [numberFormat] = useState('es-MX');
  const [currency] = useState('MXN');
  const [dateError, setDateError] = useState(null);
  const [expandedCheque, setExpandedCheque] = useState(null);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedCheque, setSelectedCheque] = useState(null);
  const taxRate = 0.16;

  const formatCurrency = (value) => {
    const numericValue = parseFloat(value) || 0;
    return new Intl.NumberFormat(numberFormat, {
      style: 'currency',
      currency: currency
    }).format(numericValue);
  };

  useEffect(() => {
    const fetchCorteData = async () => {
      if (!show || !property) return;

      setLoading(true);
      setError(null);
      setDateError(null);

      try {
        const [corteResponse, salesResponse] = await Promise.all([
          fetch(`${BASE_API_URL}/lectura_cheques_corte.php?property=${encodeURIComponent(property)}`),
          fetch(`${BASE_API_URL}/lectura_cheques.php?property=${encodeURIComponent(property)}`)
        ]);

        if (!corteResponse.ok) throw new Error(`Error ${corteResponse.status} loading corte data`);
        if (!salesResponse.ok) throw new Error(`Error ${salesResponse.status} loading sales data`);

        const corteData = await corteResponse.json();
        const salesData = await salesResponse.json();

        console.log('API Response (lectura_cheques_corte):', corteData);
        console.log('API Response (lectura_cheques):', salesData);

        if (corteData.success && salesData.success) {
          if (!corteData.data || typeof corteData.data !== 'object') {
            throw new Error('Invalid corte data structure: missing or invalid data object');
          }

          corteData.data.cheques = Array.isArray(corteData.data.cheques) ?
            corteData.data.cheques.map(cheque => {
              if (!cheque.fecha_control) {
                console.warn('Cheque sin fecha_control:', cheque);
                return { ...cheque, fecha_control: '0000-00-00' };
              }
              try {
                const parsedDate = parse(cheque.fecha_control, 'yyyy-MM-dd', new Date());
                const formattedDate = format(parsedDate, 'yyyy-MM-dd');
                console.log(`Normalizing corte date: ${cheque.fecha_control} -> ${formattedDate}`);
                return { ...cheque, fecha_control: formattedDate };
              } catch (error) {
                console.error(`Error normalizing corte date ${cheque.fecha_control}:`, error);
                return { ...cheque, fecha_control: '0000-00-00' };
              }
            }) : [];

          if (!salesData.data || !Array.isArray(salesData.data)) {
            throw new Error('Invalid sales data structure: missing or invalid data array');
          }

          salesData.data = salesData.data.map(cheque => {
            if (!cheque.fecha_control) {
              console.warn('Sales cheque sin fecha_control:', cheque);
              return { ...cheque, fecha_control: '0000-00-00' };
            }
            try {
              const parsedDate = parse(cheque.fecha_control, 'yyyy-MM-dd', new Date());
              const formattedDate = format(parsedDate, 'yyyy-MM-dd');
              console.log(`Normalizing sales date: ${cheque.fecha_control} -> ${formattedDate}`);
              return { ...cheque, fecha_control: formattedDate };
            } catch (error) {
              console.error(`Error normalizing sales date ${cheque.fecha_control}:`, error);
              return { ...cheque, fecha_control: '0000-00-00' };
            }
          });

          const corteDateCounts = corteData.data.cheques.reduce((acc, cheque) => {
            if (cheque.fecha_control && cheque.fecha_control !== '0000-00-00') {
              acc[cheque.fecha_control] = (acc[cheque.fecha_control] || 0) + 1;
            }
            return acc;
          }, {});

          const salesDateCounts = salesData.data.reduce((acc, cheque) => {
            if (cheque.fecha_control && cheque.fecha_control !== '0000-00-00') {
              acc[cheque.fecha_control] = (acc[cheque.fecha_control] || 0) + 1;
            }
            return acc;
          }, {});

          const uniqueDates = [...new Set([
            ...Object.keys(corteDateCounts),
            ...Object.keys(salesDateCounts)
          ])].sort((a, b) => new Date(b) - new Date(a))
            .filter(date => (corteDateCounts[date] || 0) > 0 || (salesDateCounts[date] || 0) > 0);

          console.log('Fechas disponibles con conteo:', 
            uniqueDates.map(date => ({
              date,
              corteCount: corteDateCounts[date] || 0,
              salesCount: salesDateCounts[date] || 0
            }))
          );

          setAvailableDates(uniqueDates);
          setApiData(corteData);
          setSalesData(salesData);

          if (uniqueDates.length > 0) {
            setSelectedDate(uniqueDates[0]);
            console.log(`Mostrando datos para ${uniqueDates[0]} con ${corteDateCounts[uniqueDates[0]] || 0} cheques corte y ${salesDateCounts[uniqueDates[0]] || 0} cheques ventas`);
          } else {
            setDateError("No hay datos disponibles para ninguna fecha");
          }
        } else {
          throw new Error(corteData.message || salesData.message || 'Error in received data');
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (show) fetchCorteData();
  }, [show, property]);

  const analisis = useMemo(() => {
    if (!apiData || !apiData.data || !selectedDate || !salesData || !salesData.data) {
      console.log('No data to process: apiData, salesData or selectedDate missing', { apiData, salesData, selectedDate });
      return [];
    }

    const { cheques = [] } = apiData.data;
    const filteredCheques = cheques.filter(cheque => cheque.fecha_control === selectedDate);
    const filteredSales = salesData.data.filter(cheque => cheque.fecha_control === selectedDate);

    console.log(`Procesando ${filteredCheques.length} cheques corte y ${filteredSales.length} cheques ventas para ${selectedDate}`);

    const analisis = [];

    filteredSales.forEach(cheque => {
      const corteCheque = filteredCheques.find(c => c.id === cheque.id);
      const pagos = corteCheque ? (Array.isArray(corteCheque.pagos) ? corteCheque.pagos : []) : [];
      const propina = pagos.reduce((sum, pago) => sum + (parseFloat(pago.monto_propina) || 0), 0);
      console.log(`Cheque ${cheque.id}: pagos=`, pagos, `propina total=${propina}`);

      const revenue = cheque.revenue || 'Desconocido';
      const importe = parseFloat(cheque.total_cheque) || 0;
      const cs = parseFloat(cheque.cs) || 0;
      const cajero = corteCheque ? (corteCheque.nombre_cajero || 'Desconocido') : 'Desconocido';
      const vendedor = corteCheque ? (corteCheque.nombre_vendedor || 'Desconocido') : 'Desconocido';
      const pedidos = Array.isArray(cheque.pedidos) ? cheque.pedidos : [];
      let chequeDescuentos = 0;

      let productos = [];
      let totalSinImpuestos = 0;
      let totalImpuestos = 0;
      let totalDescuentos = 0;

      pedidos.forEach(pedido => {
        const prods = Array.isArray(pedido.productos) ? pedido.productos : [];
        prods.forEach(producto => {
          if (producto.estado === 'void') return;

          const precio = parseFloat(producto.precio) || 0;
          const cantidad = parseFloat(producto.cantidad) || 0;
          const impuesto = parseFloat(producto.impuesto) / 100 || taxRate;
          const montoDescuento = parseFloat(producto.monto_descuento) || 0;
          const totalProducto = precio * cantidad - montoDescuento;
          const impuestosProducto = totalProducto * impuesto / (1 + impuesto);
          const sinImpuestos = totalProducto - impuestosProducto;

          productos.push({
            nombre: producto.nombre,
            cantidad,
            sinImpuestos,
            impuestos: impuestosProducto,
            descuentos: montoDescuento
          });

          totalSinImpuestos += sinImpuestos;
          totalImpuestos += impuestosProducto;
          totalDescuentos += montoDescuento;
          chequeDescuentos += montoDescuento;
        });
      });

      const pagosSinPropinas = pagos.map(pago => {
        const montoTotal = parseFloat(pago.monto_total) || 0;
        const montoPropina = parseFloat(pago.monto_propina) || 0;
        const montoCambio = parseFloat(pago.monto_cambio) || 0;
        const montoSinPropina = montoTotal - montoPropina - montoCambio;
        console.log(`Pago ${pago.nombre_pago}: folio=${pago.folio}, monto_total=${montoTotal}, monto_propina=${montoPropina}, monto_cambio=${montoCambio}, montoSinPropina=${montoSinPropina}`);
        return {
          nombre_pago: pago.nombre_pago || 'Desconocido',
          folio: pago.folio || 'Sin folio',
          monto: montoSinPropina,
          monto_propina: montoPropina
        };
      });

      const metodo_pago = pagos.length > 0 ? [...new Set(pagos.map(pago => pago.nombre_pago || 'Desconocido'))].join(', ') : 'Sin pagos';
      const totalPagosSinPropinas = pagosSinPropinas.reduce((sum, pago) => sum + pago.monto, 0);
      const fueraDeBalance = importe - totalPagosSinPropinas;
      console.log(`Cheque ${cheque.id}: importe=${importe}, totalPagosSinPropinas=${totalPagosSinPropinas}, fueraDeBalance=${fueraDeBalance}`);

      analisis.push({
        id: cheque.id,
        revenue,
        metodo_pago,
        cs,
        propina,
        cajero,
        vendedor,
        productos,
        pagos: pagosSinPropinas,
        ventas: {
          total: importe,
          sinImpuestos: totalSinImpuestos,
          impuestos: totalImpuestos,
          descuentos: totalDescuentos
        },
        fueraDeBalance,
        isBalanced: Math.abs(fueraDeBalance) < 0.01
      });
    });

    return analisis;
  }, [apiData, salesData, selectedDate]);

  const analisisByRevenue = useMemo(() => {
    const byRevenue = {};
    analisis.forEach(cheque => {
      if (!byRevenue[cheque.revenue]) {
        byRevenue[cheque.revenue] = [];
      }
      byRevenue[cheque.revenue].push(cheque);
    });
    return byRevenue;
  }, [analisis]);

  const handleExpandCheque = (chequeId) => {
    setExpandedCheque(expandedCheque === chequeId ? null : chequeId);
  };

  const handleOpenAdjustDialog = (numeroCheque) => {
    setSelectedCheque(numeroCheque);
    setAdjustDialogOpen(true);
  };

  const handleCloseAdjustDialog = () => {
    setAdjustDialogOpen(false);
    setSelectedCheque(null);
  };

  const handleAdjustSuccess = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetch(`${BASE_API_URL}/lectura_cheques_corte.php?property=${encodeURIComponent(property)}`),
      fetch(`${BASE_API_URL}/lectura_cheques.php?property=${encodeURIComponent(property)}`)
    ])
      .then(([corteResponse, salesResponse]) => Promise.all([corteResponse.json(), salesResponse.json()]))
      .then(([corteData, salesData]) => {
        if (corteData.success && salesData.success) {
          setApiData(corteData);
          setSalesData(salesData);

          const corteDateCounts = corteData.data.cheques.reduce((acc, cheque) => {
            if (cheque.fecha_control && cheque.fecha_control !== '0000-00-00') {
              acc[cheque.fecha_control] = (acc[cheque.fecha_control] || 0) + 1;
            }
            return acc;
          }, {});

          const salesDateCounts = salesData.data.reduce((acc, cheque) => {
            if (cheque.fecha_control && cheque.fecha_control !== '0000-00-00') {
              acc[cheque.fecha_control] = (acc[cheque.fecha_control] || 0) + 1;
            }
            return acc;
          }, {});

          const uniqueDates = [...new Set([
            ...Object.keys(corteDateCounts),
            ...Object.keys(salesDateCounts)
          ])].sort((a, b) => new Date(b) - new Date(a));

          setAvailableDates(uniqueDates);
          if (uniqueDates.length > 0 && !selectedDate) {
            setSelectedDate(uniqueDates[0]);
          }
        } else {
          throw new Error(corteData.message || salesData.message || 'Error in received data');
        }
      })
      .catch(error => {
        console.error('Error reloading data:', error);
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
        setAdjustDialogOpen(false);
        setSelectedCheque(null);
      });
  };

  return (
    <Dialog 
      open={show} 
      onClose={onClose} 
      fullWidth 
      maxWidth="xl"
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Análisis de Cheques - {property}</Typography>
          <IconButton onClick={onClose}>
            <ArrowBack />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Cargando datos del análisis...</Typography>
          </Box>
        ) : error || dateError ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography color="error">{error || dateError}</Typography>
          </Box>
        ) : (
          <>
            <Box mb={3} display="flex" alignItems="center">
              <DatePicker
                selected={selectedDate ? parse(selectedDate, 'yyyy-MM-dd', new Date()) : null}
                onChange={(date) => setSelectedDate(format(date, 'yyyy-MM-dd'))}
                dateFormat="yyyy-MM-dd"
                customInput={
                  <TextField
                    label="Fecha"
                    variant="outlined"
                    size="small"
                    error={!!dateError}
                    helperText={dateError}
                    InputProps={{
                      startAdornment: <DateRange color="action" />,
                    }}
                  />
                }
                includeDates={availableDates.map(date => parse(date, 'yyyy-MM-dd', new Date()))}
              />
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                Análisis de Cheques - {selectedDate}
              </Typography>
              {Object.entries(analisisByRevenue).map(([revenue, cheques]) => (
                <Box key={revenue} mb={4}>
                  <Typography variant="subtitle1" gutterBottom>
                    {revenue}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID Cheque</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Impuesto</TableCell>
                          <TableCell align="right">Descuento</TableCell>
                          <TableCell align="right">Fuera de Balance</TableCell>
                          <TableCell align="right">Método de Pago</TableCell>
                          <TableCell align="right">CS</TableCell>
                          <TableCell align="right">Propina</TableCell>
                          <TableCell align="right">Cajero</TableCell>
                          <TableCell align="right">Vendedor</TableCell>
                          <TableCell align="right">Neto</TableCell>
                          <TableCell align="right">Editar</TableCell>
                          <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cheques.map((cheque) => (
                          <React.Fragment key={cheque.id}>
                            <TableRow>
                              <TableCell>
                                {cheque.id}
                                {!cheque.isBalanced && (
                                  <Warning color="error" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                )}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(cheque.ventas.total)}</TableCell>
                              <TableCell align="right">{formatCurrency(cheque.ventas.impuestos)}</TableCell>
                              <TableCell align="right">{formatCurrency(cheque.ventas.descuentos)}</TableCell>
                              <TableCell align="right" sx={{ color: !cheque.isBalanced ? 'error.main' : 'inherit' }}>
                                {formatCurrency(cheque.fueraDeBalance)}
                              </TableCell>
                              <TableCell align="right">{cheque.metodo_pago}</TableCell>
                              <TableCell align="right">{formatCurrency(cheque.cs)}</TableCell>
                              <TableCell align="right">{formatCurrency(cheque.propina)}</TableCell>
                              <TableCell align="right">{cheque.cajero}</TableCell>
                              <TableCell align="right">{cheque.vendedor}</TableCell>
                              <TableCell align="right">{formatCurrency(cheque.ventas.sinImpuestos)}</TableCell>
                              <TableCell align="right">
                                <Tooltip title="Editar cheque">
                                  <IconButton 
                                    onClick={() => handleOpenAdjustDialog(cheque.id)}
                                    color="primary"
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                <Box display="flex" alignItems="center" justifyContent="flex-end">
                                  <Tooltip title="Ver detalles">
                                    <IconButton 
                                      onClick={() => handleExpandCheque(cheque.id)} 
                                      size="small"
                                    >
                                      {expandedCheque === cheque.id ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={13} sx={{ p: 0, borderBottom: expandedCheque === cheque.id ? '1px solid rgba(224, 224, 224, 1)' : 0 }}>
                                <Collapse in={expandedCheque === cheque.id} timeout="auto" unmountOnExit>
                                  <Box p={2} bgcolor="#fafafa">
                                    <Typography variant="subtitle2" gutterBottom>
                                      Detalles del Cheque {cheque.id}
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Typography variant="subtitle2" gutterBottom>
                                      Productos
                                    </Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Producto</TableCell>
                                          <TableCell align="right">Cantidad</TableCell>
                                          <TableCell align="right">Total</TableCell>
                                          <TableCell align="right">Impuestos</TableCell>
                                          <TableCell align="right">Descuentos</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {cheque.productos.map((producto, index) => (
                                          <TableRow key={index}>
                                            <TableCell>{producto.nombre}</TableCell>
                                            <TableCell align="right">{producto.cantidad}</TableCell>
                                            <TableCell align="right">{formatCurrency(producto.sinImpuestos + producto.impuestos)}</TableCell>
                                            <TableCell align="right">{formatCurrency(producto.impuestos)}</TableCell>
                                            <TableCell align="right">{formatCurrency(producto.descuentos)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                      Pagos
                                    </Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Folio</TableCell>
                                          <TableCell>Método</TableCell>
                                          <TableCell align="right">Monto</TableCell>
                                          <TableCell align="right">Propina</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {cheque.pagos.map((pago, index) => (
                                          <TableRow key={index}>
                                            <TableCell>{pago.folio}</TableCell>
                                            <TableCell>{pago.nombre_pago}</TableCell>
                                            <TableCell align="right">{formatCurrency(pago.monto)}</TableCell>
                                            <TableCell align="right">{formatCurrency(pago.monto_propina)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                        <TableRow sx={{ fontWeight: 'bold' }}>
                          <TableCell>Total {revenue}</TableCell>
                          <TableCell align="right">{formatCurrency(cheques.reduce((sum, cheque) => sum + cheque.ventas.total, 0))}</TableCell>
                          <TableCell align="right">{formatCurrency(cheques.reduce((sum, cheque) => sum + cheque.ventas.impuestos, 0))}</TableCell>
                          <TableCell align="right">{formatCurrency(cheques.reduce((sum, cheque) => sum + cheque.ventas.descuentos, 0))}</TableCell>
                          <TableCell align="right">{formatCurrency(cheques.reduce((sum, cheque) => sum + cheque.fueraDeBalance, 0))}</TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right">{formatCurrency(cheques.reduce((sum, cheque) => sum + cheque.cs, 0))}</TableCell>
                          <TableCell align="right">{formatCurrency(cheques.reduce((sum, cheque) => sum + cheque.propina, 0))}</TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right">{formatCurrency(cheques.reduce((sum, cheque) => sum + cheque.ventas.sinImpuestos, 0))}</TableCell>
                          <TableCell align="right"></TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
              <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  Gran Total - Todos los Departamentos
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Total General</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Impuesto</TableCell>
                        <TableCell align="right">Descuento</TableCell>
                        <TableCell align="right">Fuera de Balance</TableCell>
                        <TableCell align="right"></TableCell>
                        <TableCell align="right">CS</TableCell>
                        <TableCell align="right">Propina</TableCell>
                        <TableCell align="right"></TableCell>
                        <TableCell align="right"></TableCell>
                        <TableCell align="right">Neto</TableCell>
                        <TableCell align="right"></TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow sx={{ fontWeight: 'bold' }}>
                        <TableCell>Total</TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            Object.values(analisisByRevenue).flat().reduce((sum, cheque) => sum + cheque.ventas.total, 0)
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            Object.values(analisisByRevenue).flat().reduce((sum, cheque) => sum + cheque.ventas.impuestos, 0)
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            Object.values(analisisByRevenue).flat().reduce((sum, cheque) => sum + cheque.ventas.descuentos, 0)
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            Object.values(analisisByRevenue).flat().reduce((sum, cheque) => sum + cheque.fueraDeBalance, 0)
                          )}
                        </TableCell>
                        <TableCell align="right"></TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            Object.values(analisisByRevenue).flat().reduce((sum, cheque) => sum + cheque.cs, 0)
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            Object.values(analisisByRevenue).flat().reduce((sum, cheque) => sum + cheque.propina, 0)
                          )}
                        </TableCell>
                        <TableCell align="right"></TableCell>
                        <TableCell align="right"></TableCell>
                        <TableCell align="right">
                          {formatCurrency(
                            Object.values(analisisByRevenue).flat().reduce((sum, cheque) => sum + cheque.ventas.sinImpuestos, 0)
                          )}
                        </TableCell>
                        <TableCell align="right"></TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>

            <Dialog open={adjustDialogOpen} onClose={handleCloseAdjustDialog}>
              <DialogTitle>Ajustar Cheque</DialogTitle>
              <DialogContent>
                <AjusteChequeForm 
                  numeroCheque={selectedCheque}
                  property={property}
                  onSuccess={handleAdjustSuccess}
                  onClose={handleCloseAdjustDialog}
                />
              </DialogContent>
            </Dialog>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

CorteAdmin.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default CorteAdmin;